import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  Select,
  Spinner,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorMode,
  useTheme,
  useToast,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRotateRight } from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import { cqAPI } from "../../lib/Auth";
import "./Outreach.css";

// The outreach inbox — replies to platform drip campaigns. Deliberately its own
// page and endpoints (/outreach/threads), never the support inbox: the two systems
// are isolated end to end.

const PAGE_SIZE = 25;

const STATUS_FILTERS = [
  { value: "", label: "All" },
  { value: "ai", label: "AI handled" },
  { value: "open", label: "Needs a human" },
  { value: "closed", label: "Closed" },
];

const STATUS_BADGES = {
  ai: { label: "AI", colorScheme: "purple" },
  open: { label: "Open", colorScheme: "red" },
  closed: { label: "Closed", colorScheme: "gray" },
};

export const ThreadStatusBadge = ({ status }) => {
  const meta = STATUS_BADGES[status] || { label: status || "—", colorScheme: "gray" };
  return <Badge colorScheme={meta.colorScheme} whiteSpace="nowrap">{meta.label}</Badge>;
};

function OutreachInbox() {
  const navigate = useNavigate();
  const toast = useToast();
  const theme = useTheme();
  const { colorMode } = useColorMode();

  const [threads, setThreads] = useState([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [status, setStatus] = useState("");
  const [needsAttention, setNeedsAttention] = useState(false);
  const [page, setPage] = useState(1);

  const fetchRef = useRef(null);
  const issuedRef = useRef(0);

  const hoverBgColor = colorMode === "light" ? theme.colors.gray[50] : theme.colors.gray[700];
  const unreadBgColor = colorMode === "light" ? theme.colors.blue[50] : theme.colors.gray[700];
  const mutedColor = colorMode === "light" ? "gray.600" : "gray.400";
  const cardBorder = colorMode === "light" ? "gray.200" : "gray.600";

  const fetchThreads = useCallback(async () => {
    setIsLoading(true);
    // Only the newest request may paint — same discipline as the support inbox.
    const at = (issuedRef.current += 1);
    const stale = () => at !== issuedRef.current;
    try {
      const response = await cqAPI.get(
        `/outreach/threads?status=${encodeURIComponent(status)}` +
          `&needs_attention=${needsAttention ? "1" : ""}&page=${page}&limit=${PAGE_SIZE}&bid=`
      );
      if (stale()) return;
      if (response?.type !== "ok") {
        setLoadFailed(true);
        setThreads([]);
        setTotal(0);
        toast({ title: "Error", description: response?.msg || "Failed to load outreach threads.", status: "error", duration: 3000, isClosable: true });
        return;
      }
      setLoadFailed(false);
      setThreads(response.data?.threads || []);
      setTotal(response.data?.total || 0);
    } catch (error) {
      if (stale()) return;
      setLoadFailed(true);
      setThreads([]);
      setTotal(0);
      console.error("Error fetching outreach threads:", error);
    } finally {
      if (!stale()) setIsLoading(false);
    }
  }, [status, needsAttention, page, toast]);

  useEffect(() => { fetchRef.current = fetchThreads; }, [fetchThreads]);
  useEffect(() => { fetchThreads(); }, [fetchThreads]);

  // No realtime topic here (yet): refetch when the tab comes back, which is also
  // what makes a desktop notification click land on a fresh list.
  useEffect(() => {
    const onVisible = () => { if (!document.hidden) fetchRef.current && fetchRef.current(); };
    window.addEventListener("focus", onVisible);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", onVisible);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  const openThread = (id) => navigate(`/outreach-inbox/${id}`);
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const empty = loadFailed
    ? "These threads could not be loaded. Try again."
    : "No outreach replies match these filters.";

  return (
    <Box className="outreach-page page-pad" p={4} overflowY="auto" maxHeight="100vh" width="100%">
      <Flex justify="space-between" align="center" mb={4} wrap="wrap" gap={2}>
        <Text fontSize="xl" fontWeight="bold">Outreach Inbox</Text>
        <Button size="sm" leftIcon={<FontAwesomeIcon icon={faRotateRight} />} onClick={fetchThreads} isLoading={isLoading}>
          Refresh
        </Button>
      </Flex>

      <Flex mb={4} wrap="wrap" gap={2} align="center">
        <Select value={status} onChange={(e) => { setPage(1); setStatus(e.target.value); }} width={{ base: "100%", md: "200px" }}>
          {STATUS_FILTERS.map((option) => (
            <option key={option.value || "all"} value={option.value}>{option.label}</option>
          ))}
        </Select>
        <Button
          size="sm"
          variant={needsAttention ? "solid" : "outline"}
          flexShrink={0}
          onClick={() => { setPage(1); setNeedsAttention((v) => !v); }}
        >
          Waiting on an answer
        </Button>
      </Flex>

      {isLoading ? (
        <Flex justify="center" align="center" py={10}><Spinner color="blue.500" size="xl" /></Flex>
      ) : (
        <>
          <TableContainer display={{ base: "none", md: "block" }} borderWidth="1px" borderRadius="lg">
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th>Status</Th>
                  <Th>Practice</Th>
                  <Th>Contact</Th>
                  <Th>Campaign thread</Th>
                  <Th>Last activity</Th>
                </Tr>
              </Thead>
              <Tbody>
                {threads.length === 0 ? (
                  <Tr><Td colSpan={5}><Text fontSize="sm" color={mutedColor}>{empty}</Text></Td></Tr>
                ) : (
                  threads.map((thread) => (
                    <Tr
                      key={thread._id}
                      bg={thread.unread ? unreadBgColor : undefined}
                      fontWeight={thread.unread ? "bold" : "normal"}
                      _hover={{ bg: hoverBgColor, cursor: "pointer" }}
                      onClick={() => openThread(thread._id)}
                    >
                      <Td>
                        <Flex align="center" gap={2} wrap="wrap">
                          <ThreadStatusBadge status={thread.status} />
                          {thread.ai_paused && <Badge colorScheme="orange" variant="subtle">AI paused</Badge>}
                          {thread.needs_attention && <Badge colorScheme="red" variant="subtle">Unanswered</Badge>}
                        </Flex>
                      </Td>
                      <Td maxW="220px" overflow="hidden" textOverflow="ellipsis">{thread.business_name || "—"}</Td>
                      <Td maxW="220px" overflow="hidden" textOverflow="ellipsis">
                        {thread.contact_name || thread.email || thread.phone || "—"}
                      </Td>
                      <Td maxW="260px" overflow="hidden" textOverflow="ellipsis">{thread.subject || "—"}</Td>
                      <Td>{thread.last_message_at ? moment(thread.last_message_at).fromNow() : "—"}</Td>
                    </Tr>
                  ))
                )}
              </Tbody>
            </Table>
          </TableContainer>

          <Stack display={{ base: "flex", md: "none" }} spacing={3}>
            {threads.length === 0 ? (
              <Text fontSize="sm" color={mutedColor}>{empty}</Text>
            ) : (
              threads.map((thread) => (
                <Box
                  key={thread._id}
                  borderWidth="1px"
                  borderColor={cardBorder}
                  borderRadius="lg"
                  bg={thread.unread ? unreadBgColor : undefined}
                  p={3}
                  onClick={() => openThread(thread._id)}
                >
                  <Flex justify="space-between" align="center" gap={2} mb={1}>
                    <Flex align="center" gap={2} minW={0} wrap="wrap">
                      <ThreadStatusBadge status={thread.status} />
                      {thread.ai_paused && <Badge colorScheme="orange" variant="subtle">AI paused</Badge>}
                      {thread.needs_attention && <Badge colorScheme="red" variant="subtle">Unanswered</Badge>}
                    </Flex>
                    <Text fontSize="xs" color={mutedColor} whiteSpace="nowrap">
                      {thread.last_message_at ? moment(thread.last_message_at).fromNow() : ""}
                    </Text>
                  </Flex>
                  <Text fontWeight={thread.unread ? "bold" : "semibold"} noOfLines={1} textAlign="left">
                    {thread.business_name || "—"}
                  </Text>
                  <Text fontSize="xs" color={mutedColor} noOfLines={1} textAlign="left">
                    {[thread.contact_name || thread.email || thread.phone, thread.subject].filter(Boolean).join(" · ")}
                  </Text>
                </Box>
              ))
            )}
          </Stack>

          <Flex mt={4} align="center" justify="space-between" wrap="wrap" gap={2}>
            <Text fontSize="sm" color={mutedColor}>
              {total === 0 ? "No threads" : `Showing ${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, total)} of ${total}`}
            </Text>
            <Flex align="center" gap={2}>
              <Button size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} isDisabled={page <= 1}>Previous</Button>
              <Text fontSize="sm">Page {page} of {pageCount}</Text>
              <Button size="sm" onClick={() => setPage((p) => Math.min(pageCount, p + 1))} isDisabled={page >= pageCount}>Next</Button>
            </Flex>
          </Flex>
        </>
      )}
    </Box>
  );
}

export default OutreachInbox;
